import { useState } from 'react';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import Spacing from 'components/Spacing';
import { useLayoutContext } from 'contexts/LayoutContext';
import { Step, StepLabel, Stepper } from '@mui/material';
import { Grid } from '@mui/material';
import GridContainer from 'components/GridContainer';
import FormWrapper, { defaultHoldingClassRealisation } from './FormWrapper';
import CreateRealisation from './CreateRealisation';
import EnterConsideration from './EnterConsideration';
import {
  ConsiderationAllocationLogic,
  ConsiderationType,
} from 'further-types/lp/realisation';
import { CalendarDay } from 'further-ui/utils';
import Summary from './Summary';
import SuccessPage from './SuccessPage';

enum Steps {
  Create,
  EnterConsideration,
  Summary,
  Success,
}

const stepLabels = {
  [Steps.Create]: 'Enter realisation details',
  [Steps.EnterConsideration]: 'Enter consideration',
  [Steps.Summary]: 'Create realisation',
};

const pageTitles = {
  [Steps.Create]: 'Realisations: Create Realisation',
  [Steps.EnterConsideration]: 'Realisations: Enter Consideration',
  [Steps.Summary]: 'Realisations: Summary',
  [Steps.Success]: 'Realisations: Success',
};

const Realisation: React.FC = () => {
  const [activeStep, setActiveStep] = useState<Steps>(Steps.Create);
  const { vehicle } = useLayoutContext();

  if (!vehicle) {
    throw new Error('Vehicle not found.');
  }

  return (
    <PageContainer
      heading={pageTitles[activeStep]}
      pageTitle="Realisations: Add Realisation"
    >
      <PageContent>
        <Spacing size="lg">
          <GridContainer>
            <Grid size={{ sm: 12, md: 12, lg: 8 }}>
              <Stepper activeStep={activeStep}>
                {Object.values(stepLabels).map((stepLabel) => (
                  <Step key={stepLabel}>
                    <StepLabel>{stepLabel}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Grid>
          </GridContainer>
          <FormWrapper
            key={vehicle._id}
            selectedVehicle={vehicle}
            values={{
              vehicleId: vehicle._id,
              assetId: '',
              assetName: '',
              realisationDate: new CalendarDay(new Date()),
              holdingClassRealisation: [defaultHoldingClassRealisation],
              consideration: [
                {
                  considerationType: ConsiderationType.CashAndWithholdingTax,
                  considerationAllocationLogic:
                    ConsiderationAllocationLogic.ProRata,
                  currency: vehicle.baseCurrency,
                },
              ],
              investorWithholdingTaxList: [],
            }}
          >
            {activeStep === Steps.Create && (
              <CreateRealisation
                onSuccess={() => setActiveStep(Steps.EnterConsideration)}
                selectedVehicle={vehicle}
              />
            )}
            {activeStep === Steps.EnterConsideration && (
              <EnterConsideration
                onPrevious={() => setActiveStep(Steps.Create)}
                onNext={() => setActiveStep(Steps.Summary)}
                vehicle={vehicle}
              />
            )}
            {activeStep === Steps.Summary && (
              <Summary
                onPrevious={() => setActiveStep(Steps.EnterConsideration)}
                onSuccess={() => setActiveStep(Steps.Success)}
                selectedVehicle={vehicle}
              />
            )}
            {activeStep === Steps.Success && <SuccessPage />}
          </FormWrapper>
        </Spacing>
      </PageContent>
    </PageContainer>
  );
};

export default Realisation;
